import { format } from 'date-fns'
import { useMemo } from 'react'
import { X, ChevronLeft, ChevronRight } from 'lucide-react'
import type { FrontendLog } from '@/types/frontendLog'
import type { TrajectoryInfo } from '@/types/forklift'
import type { TaskRun } from '@/types/robotEvents'
import { LEVEL_COLORS, LEVEL_LABELS } from './LogTable'
import {
  getErrorDescription,
  getErrorTitle,
  getErrorDetails,
} from '@/common/errorDescriptions'
import PreviewPathCanvas from '@/components/maps/PreviewPathCanvas'
import ObstacleSensorCanvas from './ObstacleSensorCanvas'
import { parseObstacleHandlerFromContext } from '@/types/obstacle'
import { getExecutionModeLabelFromLog } from '@/common/scenarioLabel'
import { getAppLabelFromLog } from '@/common/appLabel'
import { useScenarioTrajectory } from '@/hooks/useScenarioTrajectory'

interface LogDetailModalProps {
  log: FrontendLog
  onClose: () => void
  logs?: FrontendLog[]
  onNavigate?: (log: FrontendLog) => void
  // このログ (scenario_execute) に対応する機体ジャーナルの実行。実走行パスの表示に使う
  // (PLC 起動のログは予定ルートを持たないため、これが唯一のパス情報になる)
  run?: TaskRun
}

// シナリオ実行開始のログかどうか判定
const isScenarioStart = (log: FrontendLog) =>
  log.action === 'scenario_execute' && log.message === 'シナリオ実行開始'

export default function LogDetailModal({
  log,
  onClose,
  logs,
  onNavigate,
  run,
}: LogDetailModalProps) {
  const formatTimestamp = (timestamp: string) => {
    return format(new Date(timestamp), 'yyyy-MM-dd HH:mm:ss.SSS')
  }

  // シナリオログのみ抽出
  const scenarioLogs = useMemo(() => {
    if (!logs) return []
    return logs.filter(isScenarioStart)
  }, [logs])

  // 現在のログがシナリオログの場合、そのインデックスを取得
  const currentScenarioIndex = useMemo(() => {
    if (!isScenarioStart(log)) return -1
    return scenarioLogs.findIndex((l) => l.log_id === log.log_id)
  }, [log, scenarioLogs])

  // 前後のシナリオへナビゲート
  const handlePrevScenario = () => {
    if (currentScenarioIndex > 0 && onNavigate) {
      onNavigate(scenarioLogs[currentScenarioIndex - 1])
    }
  }

  const handleNextScenario = () => {
    if (currentScenarioIndex < scenarioLogs.length - 1 && onNavigate) {
      onNavigate(scenarioLogs[currentScenarioIndex + 1])
    }
  }

  const hasPrevScenario = currentScenarioIndex > 0
  const hasNextScenario =
    currentScenarioIndex >= 0 && currentScenarioIndex < scenarioLogs.length - 1

  // シナリオ実行開始ログのルートプレビュー。
  // context.trajectories が残っていればそれを使い、ロールアップで落とされている
  // 場合は退避テーブルから log_id 単位で取得する。
  const inlineTrajectories =
    isScenarioStart(log) && Array.isArray(log.context?.trajectories)
      ? (log.context!.trajectories as TrajectoryInfo[])
      : null
  const needTrajectoryFetch = isScenarioStart(log) && !inlineTrajectories
  const { data: fetchedTrajectory, isLoading: isTrajectoryLoading } =
    useScenarioTrajectory(log.robot_id, log.log_id, needTrajectoryFetch)
  const previewTrajectories =
    inlineTrajectories ?? fetchedTrajectory?.trajectories ?? null
  // 機体ジャーナル (または 30分バケットからの再構成) が記録した実走行パス
  const runPath = run?.path && run.path.length > 1 ? run.path : null
  // 予定ルートが無い (PLC 起動など) 場合は実走行パスだけを出し、空のプレビュー枠は出さない
  const showPlannedPreview =
    isScenarioStart(log) &&
    (isTrajectoryLoading ||
      (previewTrajectories != null &&
        (previewTrajectories.length > 0 || !runPath)))

  // 障害物エラーの場合、発生時の反応センサー・障害物位置を図示する
  const obstacleInfo = parseObstacleHandlerFromContext(log.context)

  // 機体エラー（error_state）の集約情報。ログ時刻が初回発生、last_timestamp_jst が最終発生
  const deviceErrorInfo = useMemo(() => {
    if (log.category !== 'device' || log.action !== 'error_state') return null
    const ctx = log.context ?? {}
    const count = typeof ctx.count === 'number' ? ctx.count : 1
    // 最終発生時刻。last_timestamp_jst（日本時間 "yyyy-MM-dd HH:mm:ss.SSS"）を優先し、
    // 旧形式データの last_timestamp（UTC ISO）にフォールバック
    const lastTimestampLabel =
      typeof ctx.last_timestamp_jst === 'string'
        ? ctx.last_timestamp_jst.slice(11, 19)
        : typeof ctx.last_timestamp === 'string'
          ? format(new Date(ctx.last_timestamp), 'HH:mm:ss')
          : null
    const rawPose = ctx.pose as { x?: unknown; y?: unknown; theta?: unknown }
    const pose =
      rawPose && typeof rawPose.x === 'number' && typeof rawPose.y === 'number'
        ? {
            x: rawPose.x,
            y: rawPose.y,
            theta: typeof rawPose.theta === 'number' ? rawPose.theta : 0,
          }
        : null
    if (count <= 1 && !pose) return null
    return { count, lastTimestampLabel, pose }
  }, [log])

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* ヘッダー */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <span
              className="px-2 py-1 rounded text-xs font-medium text-white"
              style={{ backgroundColor: LEVEL_COLORS[log.level] }}
            >
              {LEVEL_LABELS[log.level]}
            </span>
            <span className="text-lg font-semibold text-gray-900">
              ログ詳細
            </span>
          </div>
          <div className="flex items-center gap-2">
            {/* シナリオナビゲーション */}
            {scenarioLogs.length > 1 && currentScenarioIndex >= 0 && (
              <div className="flex items-center gap-1 mr-2">
                <button
                  onClick={handlePrevScenario}
                  disabled={!hasPrevScenario}
                  className={`p-1.5 rounded transition-colors ${
                    hasPrevScenario
                      ? 'hover:bg-gray-100 text-gray-600'
                      : 'text-gray-300 cursor-not-allowed'
                  }`}
                  title="前のシナリオ"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <span className="text-sm text-gray-500">
                  {currentScenarioIndex + 1} / {scenarioLogs.length}
                </span>
                <button
                  onClick={handleNextScenario}
                  disabled={!hasNextScenario}
                  className={`p-1.5 rounded transition-colors ${
                    hasNextScenario
                      ? 'hover:bg-gray-100 text-gray-600'
                      : 'text-gray-300 cursor-not-allowed'
                  }`}
                  title="次のシナリオ"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            )}
            <button
              onClick={onClose}
              className="p-1 hover:bg-gray-100 rounded-full transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
        </div>

        {/* コンテンツ */}
        <div className="px-6 py-4 overflow-y-auto max-h-[calc(80vh-120px)]">
          <dl className="space-y-4">
            <div>
              <dt className="text-sm font-medium text-gray-500">時刻</dt>
              <dd className="mt-1 text-sm text-gray-900">
                {formatTimestamp(log.timestamp)}
              </dd>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <dt className="text-sm font-medium text-gray-500">カテゴリ</dt>
                <dd className="mt-1 text-sm text-gray-900">{log.category}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">
                  アクション
                </dt>
                <dd className="mt-1 text-sm text-gray-900">{log.action}</dd>
              </div>
              {(() => {
                const appLabel = getAppLabelFromLog(log)
                return appLabel ? (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">
                      アプリ
                    </dt>
                    <dd className="mt-1 text-sm text-gray-900">{appLabel}</dd>
                  </div>
                ) : null
              })()}
            </div>

            <div>
              <dt className="text-sm font-medium text-gray-500">メッセージ</dt>
              <dd className="mt-1 text-sm text-gray-900">
                {log.message}
                {(() => {
                  const modeLabel = getExecutionModeLabelFromLog(log)
                  return modeLabel ? (
                    <span className="ml-2 px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700">
                      {modeLabel}
                    </span>
                  ) : null
                })()}
              </dd>
            </div>

            {/* 対応する機体ジャーナルの実行があれば実走行パスを表示 */}
            {isScenarioStart(log) && runPath && (
              <div>
                <dt className="text-sm font-medium text-gray-500">
                  実走行パス
                  {run?.reconstructed && (
                    <span
                      className="ml-2 inline-flex px-2 py-0.5 rounded text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200"
                      title="機体ジャーナル対応前の実行。30分バケットの機体位置サンプルから再構成した経路"
                    >
                      再構成
                    </span>
                  )}
                </dt>
                <dd className="mt-1">
                  <PreviewPathCanvas
                    trajectories={[
                      {
                        id: run!.key,
                        slots: [],
                        action: 'run',
                        path: runPath,
                      },
                    ]}
                  />
                </dd>
              </div>
            )}

            {/* シナリオ実行開始のログはルートプレビューを表示（退避テーブルからの遅延取得を含む） */}
            {showPlannedPreview ? (
              <div>
                <dt className="text-sm font-medium text-gray-500">
                  プレビューパス
                </dt>
                <dd className="mt-1">
                  {previewTrajectories ? (
                    <PreviewPathCanvas trajectories={previewTrajectories} />
                  ) : (
                    <div className="flex items-center justify-center h-40 bg-gray-100 rounded-lg text-gray-500 text-sm">
                      ルートプレビューを読み込み中...
                    </div>
                  )}
                </dd>
              </div>
            ) : null}

            {/* エラーコードがある場合、日本語の説明を表示 */}
            {log.context?.errorCodes != null &&
            Array.isArray(log.context.errorCodes) &&
            log.context.errorCodes.length > 0 ? (
              <div>
                <dt className="text-sm font-medium text-red-600">
                  エラーコード詳細
                </dt>
                <dd className="mt-1 space-y-3">
                  {(log.context.errorCodes as number[]).map((code) => {
                    const description = getErrorDescription(code)
                    const title = getErrorTitle(description)
                    const details = getErrorDetails(description)
                    return (
                      <div
                        key={code}
                        className="bg-red-50 border border-red-200 rounded-lg p-3"
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <span className="px-2 py-0.5 bg-red-600 text-white text-xs font-mono rounded">
                            {code}
                          </span>
                          <span className="font-medium text-red-800">
                            {title}
                          </span>
                        </div>
                        {details && (
                          <p className="text-sm text-red-700 whitespace-pre-line">
                            {details}
                          </p>
                        )}
                      </div>
                    )
                  })}
                </dd>
              </div>
            ) : null}

            {/* 障害物エラーの検知状況（フォークアイコン・反応センサー・障害物位置） */}
            {obstacleInfo && (
              <div>
                <dt className="text-sm font-medium text-gray-500">
                  障害物検知状況
                </dt>
                <dd className="mt-1">
                  <ObstacleSensorCanvas
                    obstacleHandler={obstacleInfo.obstacleHandler}
                    pose={obstacleInfo.pose}
                  />
                </dd>
              </div>
            )}

            {/* 機体エラーの集約情報（初回発生〜最終発生・回数・初回発生位置） */}
            {deviceErrorInfo && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800 space-y-1">
                {deviceErrorInfo.count > 1 && (
                  <div>
                    <span className="font-medium">
                      同一エラーが {deviceErrorInfo.count} 回連続
                    </span>
                    <span className="text-amber-600 ml-2">
                      （{format(new Date(log.timestamp), 'HH:mm:ss')} 〜{' '}
                      {deviceErrorInfo.lastTimestampLabel ?? '-'}）
                    </span>
                  </div>
                )}
                {deviceErrorInfo.pose && (
                  <div>
                    <span className="font-medium">初回発生位置</span>
                    <span className="ml-2 font-mono">
                      x={deviceErrorInfo.pose.x.toFixed(2)} y=
                      {deviceErrorInfo.pose.y.toFixed(2)} θ=
                      {((deviceErrorInfo.pose.theta * 180) / Math.PI).toFixed(
                        1
                      )}
                      °
                    </span>
                  </div>
                )}
              </div>
            )}

            {log.context && Object.keys(log.context).length > 0 && (
              <div>
                <dt className="text-sm font-medium text-gray-500">
                  コンテキスト
                </dt>
                <dd className="mt-1">
                  <pre className="text-xs bg-gray-100 p-3 rounded-lg overflow-x-auto">
                    {JSON.stringify(log.context, null, 2)}
                  </pre>
                </dd>
              </div>
            )}

            {log.error && (
              <div>
                <dt className="text-sm font-medium text-red-600">エラー情報</dt>
                <dd className="mt-1 space-y-2">
                  <div className="text-sm">
                    <span className="font-medium text-red-700">
                      {log.error.name}:
                    </span>{' '}
                    <span className="text-red-600">{log.error.message}</span>
                  </div>
                  {log.error.stack && (
                    <pre className="text-xs bg-red-50 text-red-800 p-3 rounded-lg overflow-x-auto whitespace-pre-wrap">
                      {log.error.stack}
                    </pre>
                  )}
                </dd>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4 pt-2 border-t border-gray-200">
              <div>
                <dt className="text-sm font-medium text-gray-500">サイトID</dt>
                <dd className="mt-1 text-sm text-gray-900">{log.site_id}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">
                  ロボットID
                </dt>
                <dd className="mt-1 text-sm text-gray-900">{log.robot_id}</dd>
              </div>
            </div>

            {(log.platform || log.app_version || log.session_id) && (
              <div className="grid grid-cols-3 gap-4">
                {log.platform && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">
                      プラットフォーム
                    </dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {log.platform}
                    </dd>
                  </div>
                )}
                {log.app_version && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">
                      アプリバージョン
                    </dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {log.app_version}
                    </dd>
                  </div>
                )}
                {log.session_id && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">
                      セッションID
                    </dt>
                    <dd
                      className="mt-1 text-xs text-gray-900 font-mono truncate"
                      title={log.session_id}
                    >
                      {log.session_id}
                    </dd>
                  </div>
                )}
              </div>
            )}

            <div className="pt-2 border-t border-gray-200">
              <dt className="text-sm font-medium text-gray-500">ログID</dt>
              <dd className="mt-1 text-xs text-gray-500 font-mono">
                {log.log_id}
              </dd>
            </div>
          </dl>
        </div>
      </div>
    </div>
  )
}
